import { Modal } from "react-bootstrap";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import UserIcon from "../../assets/img/user-icon.svg";
import IncomeSourceCard from "./IncomeSourceCard";
import CustomIncomeSourceCard from "./CustomIncomeSourceCard";
import useTranslator from "../../hooks/useTranslator";
import { useTranslation } from "react-i18next";
import { convertCurrencyFormat, haveValue } from "../../helper/common";
import { useCallback, useEffect, useMemo, useState } from "react";

const NON_INCOME_SOURCE_KEYS = [
  "name",
  "_id",
  "forensic_status",
  "step",
  "user_email",
  "profile_completed",
  "isForensicExists",
  "declaredIncome",
];
const INCOME_MODE_SOURCE_PREFIX = "__income_mode:";
const DEFAULT_INCOME_MODE = "avg";
const CUSTOM_INCOME_PREFIX = "custom_income:";

const isIncomeModeSource = (source) =>
  typeof source === "string" && source.startsWith(INCOME_MODE_SOURCE_PREFIX);

const isCustomIncomeSource = (source) =>
  typeof source === "string" && source.startsWith(CUSTOM_INCOME_PREFIX);

const isNewCustomIncomeSource = (source) =>
  isCustomIncomeSource(source) && source.startsWith("custom_income:custom_");

const getSelectionArray = (selection) => {
  if (!haveValue(selection)) return [];

  return Array.isArray(selection) ? selection : [selection];
};

const getSelectionMode = (selection, fallbackMode = DEFAULT_INCOME_MODE) => {
  const modeSource = getSelectionArray(selection).find(isIncomeModeSource);
  const mode = modeSource?.replace(INCOME_MODE_SOURCE_PREFIX, "");

  return mode === "sum" || mode === "avg" ? mode : fallbackMode;
};

const getSelectionSources = (selection) =>
  getSelectionArray(selection).filter((source) => !isIncomeModeSource(source));

const buildPersistedSelection = (selection, mode) => {
  const sources = getSelectionSources(selection);

  return mode === "sum" && sources.length > 1
    ? [...sources, `${INCOME_MODE_SOURCE_PREFIX}${mode}`]
    : sources;
};

const getMonthlySourceAmount = (source, amount) => {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) return 0;

  if (
    source?.startsWith("payslip:") ||
    source === "declaredIncome" ||
    source === "bank" ||
    isCustomIncomeSource(source)
  ) {
    return Math.round(numericAmount);
  }

  return Math.round(numericAmount / 12);
};

const getIncomeSourceKeys = (sources = {}) =>
  Object.keys(sources).filter((source) => {
    if (NON_INCOME_SOURCE_KEYS.includes(source)) return false;
    if (isCustomIncomeSource(source)) return true;

    const value = sources[source];
    if (
      (source === "itr" || source === "bank") &&
      (value <= 0 || isNaN(value))
    ) {
      return false;
    }

    return true;
  });

let customIncomeCounter = 0;

const generateCustomIncomeId = () => {
  customIncomeCounter += 1;
  return `custom_${Date.now()}_${customIncomeCounter}`;
};

const UpdatedIncomeSourceModal = ({
  show,
  onHide,
  updateSourceList,
  handleIncomeChange,
  loadFullInfo,
  savedCustomSources,
  handleDeleteCustomIncome,
  isGuarantor,
}) => {
  const { _t } = useTranslator();
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const guarantor = params.get("guarantor");

  const [selectedSource, setSelectedSources] = useState({});
  const [originalSourceMap, setOriginalSourceMap] = useState({});
  const [originalModeMap, setOriginalModeMap] = useState({});
  const [incomeModeByUser, setIncomeModeByUser] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [customIncomes, setCustomIncomes] = useState({});
  const [deletingSources, setDeletingSources] = useState({});
  const [pendingDeletions, setPendingDeletions] = useState([]);

  const getCustomSourcesForUser = useCallback(
    (userId, userType) => {
      if (!savedCustomSources) return [];

      if (userType === "tenant") {
        const tenantEntry = savedCustomSources[userId];
        const tenantSources = tenantEntry?.tenant_source;
        return Array.isArray(tenantSources) ? tenantSources : [];
      }

      for (const tenantEntry of Object.values(savedCustomSources)) {
        const guarantorSources = tenantEntry?.guarantor_sources?.[userId];
        if (Array.isArray(guarantorSources)) {
          return guarantorSources;
        }
      }

      return [];
    },
    [savedCustomSources]
  );

  const getSourcesForUser = useCallback(
    (userId, userType) => {
      const baseSources = updateSourceList.find(
        (u) => u._id === userId
      )?.all_sources;
      if (!baseSources) return {};

      const customSources = getCustomSourcesForUser(userId, userType);

      const merged = { ...baseSources };
      customSources.forEach((src) => {
        const key = `${CUSTOM_INCOME_PREFIX}${src.name}`;
        merged[key] = src.amount;
      });

      return merged;
    },
    [updateSourceList, getCustomSourcesForUser]
  );

  useEffect(() => {
    if (!updateSourceList || !updateSourceList.length) return;

    const sourceMap = {};
    const modeMap = {};

    updateSourceList.forEach((item) => {
      const baseSelected = getSelectionSources(item.current_selected);
      const mergedSources = getSourcesForUser(item._id, item.type);
      const customSourceKeys = Object.keys(mergedSources).filter(
        (k) => isCustomIncomeSource(k) && !isNewCustomIncomeSource(k)
      );

      const filteredSelected = baseSelected.filter((s) => {
        if (isCustomIncomeSource(s) && !isNewCustomIncomeSource(s)) {
          return customSourceKeys.includes(s);
        }
        return true;
      });

      sourceMap[item._id] = filteredSelected;
      modeMap[item._id] = getSelectionMode(
        item.current_selected,
        item.current_income_mode
      );
    });

    setSelectedSources(sourceMap);
    setOriginalSourceMap(sourceMap);
    setOriginalModeMap(modeMap);
    setIncomeModeByUser(modeMap);
    setCustomIncomes({});
  }, [updateSourceList, getSourcesForUser]);

  const toggleSelectedSource = (userId, newSource) => {
    setSelectedSources((prev) => {
      const current = prev[userId] || [];

      return {
        ...prev,
        [userId]: current.includes(newSource)
          ? current.filter((s) => s !== newSource)
          : [...current, newSource],
      };
    });
  };

  const addCustomIncome = (userId) => {
    const id = generateCustomIncomeId();
    const sourceKey = `${CUSTOM_INCOME_PREFIX}${id}`;

    setCustomIncomes((prev) => ({
      ...prev,
      [userId]: [
        ...(prev[userId] || []),
        { id, sourceKey, title: "", amount: "" },
      ],
    }));

    setSelectedSources((prev) => {
      const current = prev[userId] || [];

      return {
        ...prev,
        [userId]: [...current, sourceKey],
      };
    });
  };

  const updateCustomIncome = (userId, incomeId, field, value) => {
    setCustomIncomes((prev) => ({
      ...prev,
      [userId]: (prev[userId] || []).map((income) =>
        income.id === incomeId ? { ...income, [field]: value } : income
      ),
    }));
  };

  const removeCustomIncome = (userId, incomeId) => {
    setCustomIncomes((prev) => {
      const userIncomes = prev[userId] || [];
      const income = userIncomes.find((inc) => inc.id === incomeId);

      if (income) {
        setSelectedSources((prevSel) => {
          const current = prevSel[userId] || [];

          return {
            ...prevSel,
            [userId]: current.filter((s) => s !== income.sourceKey),
          };
        });
      }

      return {
        ...prev,
        [userId]: userIncomes.filter((inc) => inc.id !== incomeId),
      };
    });
  };

  const getCustomIncomeBySourceKey = useCallback(
    (sourceKey) => {
      for (const userIncomes of Object.values(customIncomes)) {
        const found = userIncomes.find((inc) => inc.sourceKey === sourceKey);
        if (found) return found;
      }
      return null;
    },
    [customIncomes]
  );

  const hasDuplicateTitles = useMemo(() => {
    const result = {};

    Object.entries(customIncomes).forEach(([userId, incomes]) => {
      const existingCustomSources = getCustomSourcesForUser(
        userId,
        updateSourceList.find((u) => u._id === userId)?.type
      );
      const existingTitles = existingCustomSources.map((src) =>
        src.name.trim().toLowerCase()
      );

      const titleCount = {};
      incomes.forEach((income) => {
        const normalizedTitle = income.title.trim().toLowerCase();
        if (normalizedTitle) {
          titleCount[normalizedTitle] = (titleCount[normalizedTitle] || 0) + 1;
        }
      });

      result[userId] = incomes.map((income) => {
        const normalizedTitle = income.title.trim().toLowerCase();
        const hasNewDuplicate = normalizedTitle
          ? titleCount[normalizedTitle] > 1
          : false;
        const hasExistingDuplicate = normalizedTitle
          ? existingTitles.includes(normalizedTitle)
          : false;
        return hasNewDuplicate || hasExistingDuplicate;
      });
    });

    return result;
  }, [customIncomes, getCustomSourcesForUser, updateSourceList]);

  const hasAnyDuplicateTitle = useMemo(() => {
    return Object.values(hasDuplicateTitles).some((duplicates) =>
      duplicates.some(Boolean)
    );
  }, [hasDuplicateTitles]);

  const hasChanges = useMemo(() => {
    return updateSourceList.some((user) => {
      const original = originalSourceMap[user._id] || [];
      const updated = selectedSource[user._id] || [];
      const originalMode = originalModeMap[user._id] || DEFAULT_INCOME_MODE;
      const updatedMode = incomeModeByUser[user._id] || DEFAULT_INCOME_MODE;

      const hasCustomChanges = (customIncomes[user._id] || []).length > 0;

      return (
        JSON.stringify(original) !== JSON.stringify(updated) ||
        originalMode !== updatedMode ||
        hasCustomChanges
      );
    });
  }, [
    incomeModeByUser,
    originalModeMap,
    selectedSource,
    originalSourceMap,
    updateSourceList,
    customIncomes,
  ]);

  const tenantIncomeByUser = useMemo(() => {
    return updateSourceList.reduce((acc, userObj) => {
      const userSelectedSources = selectedSource[userObj._id] || [];
      const mode = incomeModeByUser[userObj._id] || "avg";
      const userSources = getSourcesForUser(userObj._id, userObj.type);
      const selectedAmounts = userSelectedSources.map((source) => {
        if (isNewCustomIncomeSource(source)) {
          const customIncome = getCustomIncomeBySourceKey(source);
          return getMonthlySourceAmount(source, customIncome?.amount || 0);
        }
        return getMonthlySourceAmount(source, userSources?.[source]);
      });
      const sum = selectedAmounts.reduce((total, amount) => total + amount, 0);
      const amount =
        mode === "avg" && selectedAmounts.length
          ? Math.round(sum / selectedAmounts.length)
          : sum;

      acc[userObj._id] = {
        amount,
        mode,
        selectedCount: selectedAmounts.length,
      };

      return acc;
    }, {});
  }, [
    incomeModeByUser,
    selectedSource,
    updateSourceList,
    getCustomIncomeBySourceKey,
    getSourcesForUser,
  ]);

  const combinedIncome = useMemo(() => {
    return Object.values(tenantIncomeByUser).reduce(
      (total, tenantIncome) => total + tenantIncome.amount,
      0
    );
  }, [tenantIncomeByUser]);

  const hasEmptySelection = useMemo(() => {
    return updateSourceList.some((user) => {
      return (selectedSource[user._id] || []).length === 0;
    });
  }, [selectedSource, updateSourceList]);

  const hasInvalidCustomIncomes = useMemo(() => {
    return Object.values(customIncomes)
      .flat()
      .some((income) => !income.title.trim() || Number(income.amount) <= 0);
  }, [customIncomes]);

  const updateTenantIncomeMode = (userId, mode) => {
    setIncomeModeByUser((prev) => ({
      ...prev,
      [userId]: mode,
    }));
  };

  const getSavedSourceLabel = (key) => {
    const name = key.replace(CUSTOM_INCOME_PREFIX, "");
    return name;
  };

  const handleDeleteSavedCustomSource = async (userId, userType, key) => {
    const name = key.replace(CUSTOM_INCOME_PREFIX, "");
    const customSources = getCustomSourcesForUser(userId, userType);
    const found = customSources.find((s) => s.name === name);
    if (found && found._id) {
      const customSourceId = found._id?.$oid || found._id;
      setPendingDeletions((prev) => [
        ...prev,
        { userId, userType, key, customSourceId },
      ]);
    }
  };

  const confirmPendingDeletions = async () => {
    if (pendingDeletions.length === 0) return;

    const deleteKeys = pendingDeletions.map(
      (d) => `${d.userId}_${d.customSourceId}`
    );

    setDeletingSources((prev) => {
      const updated = { ...prev };
      deleteKeys.forEach((key) => {
        updated[key] = true;
      });
      return updated;
    });

    try {
      for (const deletion of pendingDeletions) {
        await handleDeleteCustomIncome(
          deletion.customSourceId,
          deletion.userType,
          deletion.userId
        );
      }
      setPendingDeletions([]);
      loadFullInfo();
    } catch (err) {
      console.error("Failed to delete custom incomes", err);
    } finally {
      setDeletingSources((prev) => {
        const updated = { ...prev };
        deleteKeys.forEach((key) => {
          updated[key] = false;
        });
        return updated;
      });
    }
  };

  const cancelPendingDeletion = (key) => {
    setPendingDeletions((prev) => prev.filter((d) => d.key !== key));
  };

  const initiateSourceChange = async () => {
    if (
      isSaving ||
      hasEmptySelection ||
      hasAnyDuplicateTitle ||
      hasInvalidCustomIncomes
    )
      return;

    const changedUsers = updateSourceList.filter((user) => {
      const original = originalSourceMap[user._id] || [];
      const updated = selectedSource[user._id] || [];
      const originalMode = originalModeMap[user._id] || DEFAULT_INCOME_MODE;
      const updatedMode = incomeModeByUser[user._id] || DEFAULT_INCOME_MODE;

      const hasCustomChanges = (customIncomes[user._id] || []).length > 0;

      return (
        JSON.stringify(original) !== JSON.stringify(updated) ||
        originalMode !== updatedMode ||
        hasCustomChanges
      );
    });

    const hasPendingDeletions = pendingDeletions.length > 0;
    const hasModifications = changedUsers.length > 0;

    if (!hasModifications && !hasPendingDeletions) {
      onHide();
      return;
    }

    try {
      setIsSaving(true);

      // Step 1: Add custom incomes (if any)
      if (hasModifications) {
        const customIncomePayload = {};
        Object.entries(customIncomes).forEach(([userId, incomes]) => {
          if (incomes.length > 0) {
            customIncomePayload[userId] = incomes.map((inc) => ({
              id: inc.id,
              title: inc.title.trim(),
              amount: Number(inc.amount) || 0,
              sourceKey: inc.sourceKey,
            }));
          }
        });

        const requests = changedUsers.map((user) =>
          handleIncomeChange(
            buildPersistedSelection(
              selectedSource[user._id],
              incomeModeByUser[user._id] || DEFAULT_INCOME_MODE
            ),
            isGuarantor ? true : user.type === "guarantor",
            haveValue(guarantor) ? guarantor : user._id,
            customIncomePayload[user._id] || []
          )
        );

        await Promise.all(requests);
      }

      // Step 2: Confirm pending deletions (if any)
      if (hasPendingDeletions) {
        await confirmPendingDeletions();
      }

      loadFullInfo();

      setOriginalSourceMap(selectedSource);
      setOriginalModeMap(incomeModeByUser);

      onHide();
    } catch (error) {
      console.error("Income source update failed:", error);
      setIsSaving(false);
    }
  };

  const getTenantIncomeMeta = (tenantIncome) => {
    if (tenantIncome.selectedCount === 1) {
      return `1 ${_t("source")}`;
    }

    if (tenantIncome.mode === "sum") {
      return `${_t("sumOf")} ${tenantIncome.selectedCount}`;
    }

    return `${_t("avgOf")} ${tenantIncome.selectedCount}`;
  };

  const getInitials = (name = "") =>
    name
      .trim()
      .split(/\s+/)
      .map((word) => word[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("");

  return (
    <Modal
      show={show}
      className="metermodal update-income-source-modal modal-xl modalWidth_set updateSourceModal"
      centered
      onHide={onHide}
    >
      <div className="update-income-source-modal-padding">
        <h4 className="custom-modal-heading">{_t("updateIncomeSource")}</h4>
        <p>{_t("updateIncomeSourceModelDescription")}</p>
        <ClearOutlinedIcon
          className="close-button-modal"
          onClick={onHide}
          style={{ cursor: "pointer" }}
        />
      </div>
      {updateSourceList.map((userObj) => {
        const tenantIncome = tenantIncomeByUser[userObj._id] || {
          amount: 0,
          mode: "avg",
          selectedCount: 0,
        };
        const userCustomIncomes = customIncomes[userObj._id] || [];
        const userDuplicateMap = hasDuplicateTitles[userObj._id] || [];
        const mergedSources = getSourcesForUser(userObj._id, userObj.type);

        return (
          <div
            key={userObj._id}
            className={
              userObj._id !== updateSourceList[updateSourceList.length - 1]._id
                ? "multi-user-wrapper update-income-source-modal-padding"
                : "update-income-source-modal-padding mb-4"
            }
          >
            {haveValue(userObj?.all_sources) && (
              <>
                <div className="update-income-source-user-row">
                  <div className="earner-info update-source-info user-info">
                    <div className="earner-thumbnail-wrapper">
                      {getInitials(userObj?.all_sources?.name)}
                    </div>
                    <h6 className="earner-name">
                      {userObj?.all_sources?.name}
                    </h6>
                    <div className="update-source-income-divider" />
                    <h6 className="update-source-income-value">
                      {convertCurrencyFormat(tenantIncome.amount)} €
                    </h6>
                    <span
                      className="update-source-income-meta"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {getTenantIncomeMeta(tenantIncome)}
                      {haveValue(userObj?.all_sources?.declaredIncome) && (
                        <span className="declared-income-info-icon-wrapper">
                          <span className="declared-income-info-icon">i</span>
                          <span className="declared-income-tooltip">
                            {t("declaredIncomeTooltip", {
                              amount: convertCurrencyFormat(
                                userObj.all_sources.declaredIncome
                              ),
                            })}
                          </span>
                        </span>
                      )}
                    </span>
                  </div>
                  {tenantIncome.selectedCount > 1 && (
                    <div className="income-source-mode-toggle">
                      <button
                        type="button"
                        className={tenantIncome.mode === "avg" ? "active" : ""}
                        onClick={() =>
                          updateTenantIncomeMode(userObj._id, "avg")
                        }
                      >
                        {_t("avg")}
                      </button>
                      <button
                        type="button"
                        className={tenantIncome.mode === "sum" ? "active" : ""}
                        onClick={() =>
                          updateTenantIncomeMode(userObj._id, "sum")
                        }
                      >
                        {_t("sum")}
                      </button>
                    </div>
                  )}
                </div>
                <h4 className="custom-modal-heading sourceHeading">
                  {_t(
                    "fuentesVerificadasPorSkor",
                    "Section title for verified income sources in UpdateIncomeSourceModal"
                  )}
                </h4>
              </>
            )}
            <div className="row income-source-card-main">
              {haveValue(userObj?.all_sources) ? (
                getIncomeSourceKeys(mergedSources).map((data) => {
                  const value = mergedSources[data];

                  return (
                    <div className="col-md-6 income-source-card" key={data}>
                      <IncomeSourceCard
                        userId={userObj?._id}
                        amount={value}
                        type={data}
                        selectedSource={selectedSource?.[userObj?._id]}
                        changeSelectedSource={toggleSelectedSource}
                        onDelete={
                          isCustomIncomeSource(data) &&
                          !isNewCustomIncomeSource(data)
                            ? () =>
                                handleDeleteSavedCustomSource(
                                  userObj._id,
                                  userObj.type,
                                  data
                                )
                            : undefined
                        }
                        isDeleting={
                          isCustomIncomeSource(data) &&
                          !isNewCustomIncomeSource(data)
                            ? (() => {
                                const name = data.replace(
                                  CUSTOM_INCOME_PREFIX,
                                  ""
                                );
                                const customSources = getCustomSourcesForUser(
                                  userObj._id,
                                  userObj.type
                                );
                                const found = customSources.find(
                                  (s) => s.name === name
                                );
                                const customSourceId =
                                  found?._id?.$oid || found?._id;
                                const deleteKey = `${userObj._id}_${customSourceId}`;
                                return deletingSources[deleteKey] || false;
                              })()
                            : false
                        }
                        isPendingDelete={pendingDeletions.some(
                          (p) => p.key === data
                        )}
                        onCancelPendingDelete={
                          pendingDeletions.some((p) => p.key === data)
                            ? () => cancelPendingDeletion(data)
                            : undefined
                        }
                      />
                    </div>
                  );
                })
              ) : (
                <div className="text-center my-2">
                  {_t(
                    "incomeSourceNotFound",
                    "Text used if user doesn't have any income source"
                  )}
                </div>
              )}
              {userCustomIncomes.map((income, index) => (
                <div className="col-md-6 income-source-card" key={income.id}>
                  <CustomIncomeSourceCard
                    userId={userObj?._id}
                    sourceKey={income.sourceKey}
                    selectedSource={selectedSource?.[userObj?._id]}
                    changeSelectedSource={toggleSelectedSource}
                    title={income.title}
                    amount={income.amount}
                    hasDuplicateTitle={userDuplicateMap[index] || false}
                    onTitleChange={(value) =>
                      updateCustomIncome(userObj._id, income.id, "title", value)
                    }
                    onAmountChange={(value) =>
                      updateCustomIncome(
                        userObj._id,
                        income.id,
                        "amount",
                        value
                      )
                    }
                    onRemove={() => removeCustomIncome(userObj._id, income.id)}
                  />
                </div>
              ))}
              <div className="col-md-6 income-source-card">
                <button
                  type="button"
                  className="add-custom-income-btn"
                  onClick={() => addCustomIncome(userObj._id)}
                >
                  <AddOutlinedIcon />
                  {_t("addCustomIncomeSource", "Add Custom Income Source")}
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <div className="update-income-source-footer">
        <div className="update-income-source-total">
          <div>{_t("combinedMonthlyIncome")}</div>
          <strong>{convertCurrencyFormat(combinedIncome)} €</strong>
        </div>

        {console.log("DEBUG:", {
          hasChanges: !hasChanges,
          hasEmptySelection: hasEmptySelection,
          isSaving: isSaving,
          hasAnyDuplicateTitle: hasAnyDuplicateTitle,
          hasInvalidCustomIncomes,
        })}
        <button
          className="btnNext save-changes-button"
          disabled={
            (pendingDeletions.length === 0 && !hasChanges) ||
            hasEmptySelection ||
            isSaving ||
            hasAnyDuplicateTitle ||
            hasInvalidCustomIncomes
          }
          onClick={initiateSourceChange}
        >
          {isSaving
            ? `${_t("saving")}...`
            : pendingDeletions.length > 0 && !hasChanges
              ? `${_t("delete")} ${pendingDeletions.length} ${_t(
                  pendingDeletions.length === 1 ? "item" : "items"
                )}`
              : pendingDeletions.length > 0 && hasChanges
                ? `${_t("applyChangesAndDelete")} ${pendingDeletions.length} ${_t(
                    pendingDeletions.length === 1 ? "item" : "items"
                  )}`
                : _t("apply")}
        </button>
      </div>
    </Modal>
  );
};

export default UpdatedIncomeSourceModal;
